// @ts-nocheck
import * as d3 from "d3";

import { createSvgDefs, updateFilters } from "./nexusCreateSvgDefs"
import { showConnectionInfo, hideConnectionInfo, resizeConnectionInfo } from "./nexusConnectionInfo"
import { color, layout, timing, simConfig } from "./nexusConfig"
import { CATEGORY, COLOR, CONSTANTS, FILTERS, JARGON, POSITION, POSITION_ENUM, SEX_ENUM, TENSE, TENSE_ENUM } from "../../../modules/constants";

import { wrap } from "./wrap"
import { isMSEdge, isMobile, getFrame, getNameProp, getStatusLevel, getFullStatus } from "../../../modules/utilities";

import PersonOutlineRoundedIcon from "../../../assets/images/person_outline_black_24dp.svg"
import CompanyRoundedNoColorIcon from "../../../assets/images/business_outline_black.svg"

// file global variables
const mobile = isMobile();
// let showStakeholders = true;
let svg = {
  element: {},
}

let nexus = {
  checkboxes: {},
  group: {},
  background: {},
  title: {},
  simulation: {}, // the D3 force layout simulation; it does not appear possible to add other elements to this object
  nodes: {},
  prevNodes: [],
  links: {},
  visibleNodes: [],
  visibleLinks: {},
  // linksSaturated: false,
};

let browserIsEdge = false
let showingPrompt = false
let tick = 0;
let showPosition = false
/** @type {{ d: any, parentNode: any } | null} */
let currentlyShownTooltip = null // tracks the one node currently in hover/tooltip state

export const createNexusD3 = () => {
  // console.log("createNexusD3");
  browserIsEdge = isMSEdge();
  initSvg();

  //#region create simulation -
  // NB adjust forceLink distance by number of officers, so they all fit around a cirlce
  // or adjust node sizes by number of officers
  nexus.simulation = d3.forceSimulation()

    // force that pushes/pulls LINKED nodes towards a fixed distance apart
    // NB high force value makes nodes vibrate too much
    .force("link", d3.forceLink().id(d => d.id)) // .distance(simConfig.linkDistanceFactor * nexus.nodeRadius).strength(simConfig.linkStrength))

    // force attracting/repelling ALL nodes from each other (default strength= -30)
    .force("charge", d3.forceManyBody()) // .strength(forceStrength))
    .velocityDecay(simConfig.velocityDecay)
    .alphaDecay(simConfig.alphaDecay)
    .alphaTarget(simConfig.alphaTarget)
    .alphaMin(simConfig.alphaMin)
    .on("tick", ticked);
  //#endregion create simulation
}

export const updateNexusD3 = props => {
  tick = 0;
  // console.log("nexus.nodes: ", nexus.nodes);
  const { data, handleOnClick, brandColor } = props
  // console.log("updateNexusD3")
  showPosition = data.nodes[0].entity.showPosition
  // console.log("showPosition: ", showPosition);

  hideConnectionInfo();

  // set flags that control nexus diagram
  nexus.hubNode = data.nodes.find(node => node.id === data.hubId);
  if (!nexus || !nexus.hubNode || !nexus.hubNode.entity)
    console.error("updateNexusD3-nexus problem: ", nexus)
  nexus.checkboxes = props.checkboxes

  if (CONSTANTS.MAINTAIN_POSITIONS) // only if needed, to save memory
    nexus.prevNodes = nexus.visibleNodes; // copy across before resetting

  nexus.visibleNodes = data.nodes;
  nexus.visibleLinks = data.links;
  nexus.levelCount = data.levelCount;

  // console.log("1. nexus.visibleNodes: ", nexus.visibleNodes);

  // reset the svg
  resetSvg();
  // updateNexusTitle(nexus.hubNode.entity.category);
  if (!showingPrompt)
    createPrompt()
  else
    removePrompt();

  // apply the general update pattern to the nodes
  nexus.nodes = nexus.nodes.data(nexus.visibleNodes, d => d.id); // rejoin data
  nexus.nodes.exit().select(".handler").attr("pointer-events", "none"); // disable click for nodes that are going
  nexus.nodes.exit().transition().duration(timing.slow).style("opacity", 1e-6).remove(); // exit nodes
  enterNodes(handleOnClick);
  updateNodes();

  // apply the general update pattern to the links
  nexus.links = nexus.links.data(nexus.visibleLinks, d => d.source.id + "-" + d.target.id); // rejoin data

  nexus.links.exit().transition().duration(timing.fast).style("opacity", 1e-6).remove(); // get rid of unused nodes
  enterLinks();
  updateLinks();
  resetSim();
  // console.log("2. nexus.prevNodes: ", nexus.prevNodes);
}

export const resizeNexusD3 = () => {
  // console.log("resizeD3")
  if (!nexus || !nexus.hubNode || !nexus.hubNode.entity)
    console.error("updateNexusD3-nexus problem: ", nexus)

  resetSvg(); // includes resize nexus title and past button
  updateNodes();
  updateLinks();
  resizeConnectionInfo(nexus);
  repositionPrompt();
  resetSim();
}

//#region simulation
const resetSim = () => {
  // add nodes to the simulation and reset link forces
  nexus.simulation.nodes(nexus.visibleNodes);

  nexus.simulation.force("link")
    .links(nexus.visibleLinks)
    .distance(nexus.linkDistance)
    .strength(simConfig.linkStrength);

  nexus.simulation.force("collisionForce", d3.forceCollide().radius(simConfig.collisionRadiusFactor * nexus.nodeRadius).strength(simConfig.collisionStrength));

  nexus.simulation.force("center", d3.forceCenter(0.5 * nexus.width, 0.5 * nexus.height));
  // NB makes sim jump on click due to recentering; not sure how to prevent this

  // Gently recall each node towards the angular slot assigned by setNodeAngles.js (via the node
  // ordering computed in nexusDataPrep.js) - otherwise the charge/collision/link forces above are
  // free to scramble that crossing-reducing arrangement as the simulation settles
  nexus.simulation.force("x", d3.forceX(d => targetPosition(d).x).strength(simConfig.positionRecallStrength));
  nexus.simulation.force("y", d3.forceY(d => targetPosition(d).y).strength(simConfig.positionRecallStrength));

  nexus.simulation.alpha(simConfig.alphaStart).restart();
}

const targetPosition = d => d.level === 0
  ? { x: nexus.width / 2, y: nexus.height / 2 }
  : calcNodeStartPosition(d.angle, d.level / 3)

// tick event handler
let deltaX;
let deltaY;
let hypo;

const ticked = () => {
  nexus.links.selectAll("line")
    .attr("x1", d => {
      // console.log("ticked d: ", d);
      deltaX = d.target.x - d.source.x;
      deltaY = d.target.y - d.source.y;
      hypo = Math.sqrt((deltaX * deltaX) + (deltaY * deltaY));
      // console.log("d.source.level: ", d.source.level);
      return d.source.x + (deltaX * findNodeRadius(d.source.level) / hypo);
    })
    .attr("y1", d => {
      deltaX = d.target.x - d.source.x;
      deltaY = d.target.y - d.source.y;
      hypo = Math.sqrt((deltaX * deltaX) + (deltaY * deltaY));
      return d.source.y + (deltaY * findNodeRadius(d.source.level) / hypo);
    })
    .attr("x2", d => {
      deltaX = d.target.x - d.source.x;
      deltaY = d.target.y - d.source.y;
      hypo = Math.sqrt((deltaX * deltaX) + (deltaY * deltaY));
      return d.target.x - (deltaX * (findNodeRadius(d.target.level) + (browserIsEdge ? 0 : nexus.minLinkWidth)) / hypo);
    })
    .attr("y2", d => {
      deltaX = d.target.x - d.source.x;
      deltaY = d.target.y - d.source.y;
      hypo = Math.sqrt((deltaX * deltaX) + (deltaY * deltaY));
      return d.target.y - (deltaY * (findNodeRadius(d.target.level) + (browserIsEdge ? 0 : nexus.minLinkWidth)) / hypo);
    });

  nexus.nodes.attr("transform", d => {
    // if updating, find previous position of this node and copy to current position
    let thisNode;
    if (tick === 0) { // first tick after a click/update
      if (nexus.noNewNodes && CONSTANTS.MAINTAIN_POSITIONS && nexus.prevNodes.length > 0 && (thisNode = nexus.prevNodes.find(node => node.id === d.id))) {
        console.log("maintain positions");
        d.x = thisNode.x;
        d.y = thisNode.y;
      } else if (CONSTANTS.ENABLE_INITIAL_POSITIONS) { // set initial node positions
        // console.log("node d: ", d);
        if (d.level === 0) { // hub node in centre
          // console.log("centralise hub node");
          d.x = nexus.width / 2;
          d.y = nexus.height / 2;
        } // centre
        else if (d.level >= 1) { // space evenly around hub node
          let nodeStart = calcNodeStartPosition(d.angle, d.level / 3);
          d.x = nodeStart.x;
          d.y = nodeStart.y;
        }
      }
    }

    // Keep every node's circle at least 'nodeEdgeMargin' inside the plot area - charge/collision forces
    // (and drag) can otherwise push a node's circle across or past the edge, especially with many nodes
    // or a small diagram area
    const inset = findNodeRadius(d.level) + layout.nodeEdgeMargin;
    d.x = Math.max(inset, Math.min(nexus.width - inset, d.x));
    d.y = Math.max(inset, Math.min(nexus.height - inset, d.y));

    return "translate(" + d.x + "," + d.y + ")"
  })

  ++tick;
  // console.log("tick " + tick);
}

const calcNodeStartPosition = (angle, radiusFactor) => {
  let position = {};
  const radius = nexus.size * radiusFactor;

  position.x = (nexus.width / 2) - (Math.sin(angle) * radius)
  position.y = (nexus.height / 2) + (Math.cos(angle) * radius)
  // console.log("x=" + position.x + ", y=" + position.y);
  return position;
}
//#endregion simulation

//#region nodes
const enterNodes = handleOnClick => {
  // console.log("nodes:", nexus.nodes);

  let newNodes = nexus.nodes.enter() // find the new nodes that need to be added
    .append("g")
    .attr("class", "node");

  newNodes.append("circle") // add circles
    .attr("class", "node-background")
    .attr("pointer-events", "none")
    .attr("fill", "transparent")
    .style("opacity", 0);

  /* newNodes.append("svg")
    .attr("class", "node-icon")
    .attr("viewBox", "0 0 24 24")
    .append("path")
    .attr("d", d => d.entity.category === CATEGORY.PERSON ? icon.person : icon.company)
    .attr("fill", d => getStatusColor(d.entity, d.level))
*/
  newNodes.append("svg:image")
    .attr("class", "node-icon")
    .attr("viewBox", "0 0 24 24")
    .attr("xlink:href", d => d.entity.category === CATEGORY.PERSON
      ? PersonOutlineRoundedIcon
      : CompanyRoundedNoColorIcon)
    .style("filter", d => getColorFilter(d.entity))

  newNodes.append("text") // add labels
    .attr("class", "node-text disable-select")
    .attr("text-anchor", "middle")
    // .attr("pointer-events", "none")
    .attr("font-weight", d => showPosition && (d.entity.companyProps?.lse || getPositionFontWeight(d.entity) === "bold")
      ? "bold" : "normal")
    .style("opacity", 0);

  newNodes.append("circle") // add transparent mouse handler shape on top
    .attr("class", "handler")
    .attr("fill-opacity", 0)
    .attr("stroke-width", 0)
    //***  .attr("pointer-events", "none") // create with pointer events disabled
    .on("click", function (event, d) {
      if (d.level > 0 && !d.psc) {
        // console.log("click");
        if (mobile && layout.mobileTooltip) {                 // for mobile
          if (CONSTANTS.ENABLE_LINK_TIP && d.selected !== true) {
            d.selected = true;
            showNodeTooltip(d, this.parentNode);
          } else {
            d.selected = false;
            handleOnClick(d.entity)
          }
        } else {                      // for desktop
          handleOnClick(d.entity)
        }
      }
    })
    .on("mouseover", function (event, d) {
      if (CONSTANTS.ENABLE_LINK_TIP && !mobile && d.level > 0 && !d.psc) { // only for desktop
        // console.log("mouseover")
        if (layout.hoverNodeTooltip && d.selected !== true) {
          d.selected = true;
          showNodeTooltip(d, this.parentNode);
        }
      }
    })
    .on("mouseout", function (event, d) {
      // console.log("mouseout")
      if (CONSTANTS.ENABLE_LINK_TIP && layout.hoverNodeTooltip && d.level > 0 && d.selected) {
        d.selected = false;
        hideNodeTooltip(d, this.parentNode, "slow");
      }
    })


  if (CONSTANTS.ENABLE_DRAG)
    newNodes
      .filter(d => d.level > 0) // exclude the hub node - it's pinned centrally and must not be draggable
      .call(d3.drag()
        .on("start", (event, d) => dragstarted(event, d))
        .on("drag", (event, d) => dragged(event, d))
        .on("end", (event, d) => dragended(event, d)));

  nexus.noNewNodes = newNodes._groups[0][0] === undefined;

  nexus.nodes = nexus.nodes.merge(newNodes);

  // Safety net: mouseleave on the group fires when the mouse truly exits all children,
  // catching cases where mouseout on .handler is missed during rapid multi-node movement.
  // @ts-ignore – nexus.nodes is a D3 selection at runtime despite its declared type
  nexus.nodes.on("mouseleave", (event, d) => {
    if (CONSTANTS.ENABLE_LINK_TIP && layout.hoverNodeTooltip && d.level > 0 && d.selected) {
      d.selected = false;
      hideNodeTooltip(d, event.currentTarget, "slow");
    }
  });
}

const updateNodes = event => {
  // console.log("nodes:", nexus.nodes);

  d3.selectAll(".node-tooltip").remove(); // get rid of all tooltip text

  nexus.nodes.select("circle")
    .transition()
    .duration(timing.slow)
    .attr("r", d => findNodeRadius(d.level))
    .attr("fill", d => d.level === 0
      ? COLOR.HUB
      : d.level === 1
        ? color[d.entity.category]
        : "white", // "rgb(240, 240, 240)" // "white"
    )
    .attr("stroke", "#a0a0a0")
    .attr("stroke-width", d => d.level === 0
      ? nexus.nodeRadius / 30 // 0
      : d.level === 1 // d.entity.acquired === true
        ? nexus.nodeRadius / 60
        : nexus.nodeRadius / 90) // 80 - don't discriminate nodes that have been 'acquired' - too confusing
    .style("opacity", 1)
    .style("filter", d => /* d.level === 0 || d.psc || */ browserIsEdge ? "" : "url(#node-drop-shadow)");

  nexus.nodes.select(".node-icon")
    // .transition()
    // .duration(timing.slow)
    .attr("width", d => 0.4 * findNodeRadius(d.level))
    .attr("height", d => 0.4 * findNodeRadius(d.level))
    .attr("x", d => -0.2 * findNodeRadius(d.level))
    .attr("y", d => -0.98 * findNodeRadius(d.level))
    .attr("opacity", 1)
  // .select("path")
  // .attr("fill", "yellow") // d => getStatusColor(d.entity, d.level))

  nexus.nodes.select("text")
    .text(d => {
      let nodeText = `${getNameProp.toPresent(d.entity.name)}`

      if (d.level === 1) {
        const positionText = d.entity && d.positionName
        let firstPosition = null
        if (positionText && positionText !== POSITION[POSITION_ENUM.OTHER].NAME) {
          let positions = positionText.split(",")
          firstPosition = positions[0]
        }

        if (firstPosition && firstPosition.length > 0)
          nodeText += `, ${firstPosition}`
        // else if (checkOccupation(d.id, d.entity.occupation))
        //   nodeText += `, ${d.entity.occupation}`
        else if (d.roles && d.roles[0] === JARGON.NO_ROLE)
          nodeText += ", Director"
        else if (d.roles && d.roles[0])
          nodeText += `, ${d.roles[0]}`
        else if (d.appointment && checkOccupation(d.id, d.appointment.occupation))
          nodeText += `, ${d.appointment.occupation}`
        else if (d.psc)
          nodeText += `, PSC`;
      }

      return nodeText;
    })
    .attr("font-size", d => layout.labelFontFactor * findNodeRadius(d.level))
    .style("fill", d => d.level === 0
      ? "black" // "white"
      : d.entity?.position?.typeBit > 0
        ? POSITION[d.entity.position.typeBit].COLOR
        : "black")
    .call(wrap)
    .transition()
    .duration(timing.slow)
    .style("opacity", 1);

  nexus.nodes.select(".handler")
    .attr("r", d => findNodeRadius(d.level))
    .attr("cursor", d => d.level === 0 || d.psc ? "default" : "pointer")
    //*** .attr("pointer-events", "none")
    // enable mouse action after transition delay
    .transition()
    .duration(timing.slow)
    .on("end", function () {
      d3.select(this).attr("pointer-events", "all")
    })
}
//#endregion nodes

//#region links
const enterLinks = () => {

  let newLinks = nexus.links.enter() // find the new nodes that need to be added
    .append("g")
    .attr("class", "link");

  // the wider zone around the arrow, for clicking on
  newLinks.append("line")
    .attr("class", "link-zone")
    .attr("marker-end", "url(#link-zone-head)")
    .attr("stroke", "grey")
    .style("opacity", 0)
    .on("click", function (event, d) {
      removePrompt();
      showConnectionInfo({ d, nexus, showPosition });
    })
    .on("mouseover", function () {
      if (!mobile) showLinkZone(d3.select(this));
    })
    .on("mouseout", function () {
      if (!mobile) hideLinkZone(d3.select(this))
    });

  // the line with arrow head
  newLinks.append("line")
    .attr("class", "arrow")
    .attr("pointer-events", "none")
    .attr("marker-end", browserIsEdge ? "" : // defs arrow head does not rotate with link in Edge!
      d => d.capacity === "psc"
        ? d.tense === TENSE_ENUM.CURRENT
          ? "url(#arrow-head-current-psc)"
          : "url(#arrow-head-past-psc)"
        : d.tense === TENSE_ENUM.CURRENT // || !showPosition
          ? "url(#arrow-head-current-officer)"
          : d.tense === TENSE_ENUM.NOTICE
            ? "url(#arrow-head-notice-officer)"
            : d.tense === TENSE_ENUM.PAST
              ? "url(#arrow-head-past-officer)"
              : "url(#arrow-head-future-officer)"
    )
    .attr("stroke",
      d => d.capacity === "psc"
        ? d.tense === TENSE_ENUM.CURRENT
          ? COLOR.GOOD_LIGHT
          : COLOR.BAD_LIGHT
        : //showPosition ?
        TENSE[d.tense].COLOR
      // : TENSE[TENSE_ENUM.CURRENT].COLOR // if not showing LSE data all lines are green
    )
    .style("opacity", 0);

  nexus.links = nexus.links.merge(newLinks);
}

const showLinkZone = linkZone => {
  linkZone.transition()
    .duration(timing.fast)
    .attr("cursor", "pointer")
    .style("opacity", 0.35)
}

const hideLinkZone = linkZone => {
  linkZone.transition()
    .duration(timing.fast)
    .attr("cursor", "desault")
    .style("opacity", 0)
}

const updateLinks = () => {
  const dashSize = nexus.nodeRadius * 0.1;

  nexus.links.select(".arrow")
    .transition()
    .duration(timing.slow)
    .attr("stroke-width", d => d.capacity === "psc"
      ? layout.pscLinkWidthFactor * nexus.minLinkWidth
      : nexus.minLinkWidth)
    /* .attr("stroke-width", d => d.role === "director"
  ? nexus.minLinkWidth
  : d.capacity === "psc"
    ? layout.pscLinkWidthFactor * nexus.minLinkWidth
    : layout.nonDirectorLinkWidthFactor * nexus.minLinkWidth)
*/
    .style("stroke-dasharray", d => d.role === "director"
      ? "none"
      : d.capacity === "psc"
        ? "none"
        : (`${dashSize}, ${dashSize}`))

    .style("opacity", 1);

  nexus.links.select(".link-zone")
    .transition()
    .duration(timing.slow)
    .attr("stroke-width", layout.linkZoneWidthFactor * nexus.minLinkWidth)
}
//#endregion links

//#region svg
const initSvg = () => {
  svg.element = d3.select(".nexus-div")
    .append("svg")
    .attr("class", "svg");

  createSvgDefs(svg.element)

  // initialise groups
  nexus.group = svg.element.append("g")
    .attr("id", "nexus")
    .attr("class", "nexus");

  nexus.background = nexus.group
    .append("rect")
    .attr("class", "nexusBackground")
    .on("mouseout", () => { })
    .on("mouseover", () => {
      // When the mouse reaches the background (not over any node), reset any stuck tooltip.
      if (currentlyShownTooltip) {
        currentlyShownTooltip.d.selected = false;
        hideNodeTooltip(currentlyShownTooltip.d, currentlyShownTooltip.parentNode, "slow");
      }
    });

  nexus.links = nexus.group.append("g").attr("class", "links").selectAll("g");
  nexus.nodes = nexus.group.append("g").attr("class", "nodes").selectAll("g");

  // createNexusTitle();
}

const resetSvg = () => {

  setPlotDimensions();

  const sizeFactor = nexus.size / (Math.pow((nexus.visibleNodes.length), 0.4))
  nexus.nodeRadius = sizeFactor * layout.nodeRadiusFactor // does not affect node positions
  nexus.minLinkWidth = sizeFactor * layout.linkWidthFactor

  // const linkDistanceRatio = simConfig.linkDistanceFactor * Math.pow(nexus.visibleNodes.length, 0.9)

  const linkDistanceRatio = simConfig.linkDistanceFactor * Math.max(nexus.levelCount[1], nexus.levelCount[2])

  nexus.linkDistance = Math.min(4, Math.max(2.5, linkDistanceRatio)) * nexus.nodeRadius

  // console.log("linkDistanceRatio=", linkDistanceRatio)

  updateFilters(nexus.size, nexus.nodeRadius)
  // forceStrength = -5 * nexus.size; // negative force is repulsion
}

const setPlotDimensions = () => {
  // console.log("nexus: ", nexus)
  if (!nexus || !nexus.hubNode || !nexus.hubNode.entity)
    console.error("setPlotDimensions-nexus hubNode issue: ", nexus)

  const frame = getFrame()

  nexus.width = frame.width
  nexus.height = frame.height

  // console.log("frame: ", frame)

  if (frame.portrait) { // portrait
    nexus.height = nexus.width // make nexus square on portrait / mobile
    nexus.size = nexus.height
  } else { // landscape
    nexus.size = nexus.height > nexus.width ? nexus.width : nexus.height
    if (nexus.height > nexus.width)
      nexus.height = nexus.width
  }

  svg.element.attr("width", nexus.width).attr("height", nexus.height)

  nexus.background
    .attr("width", nexus.width)
    .attr("height", nexus.height)
}

const findNodeRadius = level => nexus.nodeRadius * layout.nodeScale[level]

//#endregion svg

//#region  prompt
const createPrompt = () => {
  nexus.prompt = nexus.group.append("text")
    .attr("class", "prompt")
    .attr("font-size", 16)
    .attr("class", "disable-select nexus-title")
    .attr("x", layout.xPadding)
    // .attr("y", nexus.height - layout.yPadding)
    .attr("y", layout.yPadding) // * 1.8)
    .text(mobile
      ? "tap on circle or arrow to explore, or drag to move"
      : "click on circle or arrow to explore, or drag to move")
    .style("opacity", 1);

  showingPrompt = true;
  return;
}

const repositionPrompt = () => {
  nexus.prompt
    // .attr("y", nexus.height - layout.yPadding)
    .attr("y", layout.yPadding) //  * 1.8)
  return;
}

const removePrompt = () => {
  nexus.prompt
    .transition()
    .duration(timing.fast)
    .style("opacity", 0)
    .remove();

  return;
}
//#endregion  prompt

//#region nodeTooltip
const showNodeTooltip = (d, parentNode) => {
  // console.log("showNodeTooltip")
  if (parentNode === undefined)
    return

  // Proactively clean up any other node stuck in the hover state.
  // This guarantees a stuck node is always reset the instant a new one is hovered,
  // regardless of whether mouseout/mouseleave fired for the previous node.
  if (currentlyShownTooltip && currentlyShownTooltip.parentNode !== parentNode) {
    currentlyShownTooltip.d.selected = false;
    hideNodeTooltip(currentlyShownTooltip.d, currentlyShownTooltip.parentNode, "instant");
  }
  currentlyShownTooltip = { d, parentNode };

  // bring this node to the top of the nodes DOM tree, so it is not covered by others
  parentNode.parentNode.appendChild(parentNode);

  const tipRadius = findNodeRadius(d.level);
  // Math.max(nexus.size * layout.tooltipSizeFactor, findNodeRadius(d.level));

  // use node background as tip background, with min radius
  d3.select(parentNode).select(".node-background")
    .transition()
    .attr("r", tipRadius)
    .attr("fill", COLOR.BRAND.STD)
    .style("opacity", 0.35);

  d3.select(parentNode).select(".handler")
    .transition()
    .attr("r", tipRadius);

  // hide icon
  /* d3.select(parentNode).select(".node-icon")
    .transition()
    .attr("opacity", 0)
  */

  // hide node text
  d3.select(parentNode).select(".node-text").transition().style("opacity", 0);

  // create tip text
  const tipBoxSize = tipRadius * 0.8;

  d3.select(parentNode)
    .append("foreignObject")
    .attr("class", "node-tooltip")
    .style("pointer-events", "none")
    .attr("transform", "translate(" + (-tipBoxSize) + "," + (-0.65 * tipBoxSize) + ")")
    .attr("width", `${2 * tipBoxSize}px`)
    .attr("height", `${2.5 * tipBoxSize}px`)
    .attr("font-size", `${0.3 * tipBoxSize}px`)
    // .html(getNodeTooltipText(d))
    .html(`<p>click for more info or drag to position</p>`)
    .style("opacity", 0)
    .transition()
    .style("opacity", 1);
}

const hideNodeTooltip = (d, parentNode, speed) => {
  if (currentlyShownTooltip && currentlyShownTooltip.parentNode === parentNode) {
    currentlyShownTooltip = null;
  }

  d3.select(parentNode).select(".node-background")
    .transition()
    .duration(speed === "instant" ? 0 : timing.slow)
    .attr("r", findNodeRadius(d.level))
    .attr("fill", d => d.level === 0
      ? COLOR.HUB
      : d.level === 1
        ? color[d.entity.category]
        : "white")
    .style("opacity", 1);

  d3.select(parentNode).select(".handler")
    .transition()
    .duration(speed === "instant" ? 0 : timing.slow)
    .attr("r", findNodeRadius(d.level));

  // show icon
  /* event.select(parentNode).select(".node-icon")
    .transition()
    .duration(speed === "instant" ? 0 : timing.slow)
    .attr("opacity", 1)
  */

  // show node text
  d3.select(parentNode).select(".node-text")
    .transition()
    .duration(speed === "instant" ? 0 : timing.fast)
    .style("opacity", 1);

  // remove tip text
  d3.selectAll(".node-tooltip")
    .transition()
    .duration(speed === "instant" ? 0 : timing.slow)
    .style("opacity", 0)
    .remove();
}
//#endregion nodeTooltip

const checkOccupation = (id, occupation) =>
  id !== nexus.hubNode.id
  && occupation !== null
  && occupation !== undefined
  && occupation.toLowerCase() !== "company director"
  && occupation.toLowerCase() !== "director"
  && !occupation.toLowerCase().includes("none");

const getPositionFontWeight = entity => (entity.position?.typeBit > 0 && POSITION[entity.position.typeBit].FONT_WEIGHT === "bold") ? "bold" : "normal"

const getColorFilter = entity =>
  entity.category === CATEGORY.PERSON
    ? entity.personInfo
      ? showPosition && entity.personInfo.sex === SEX_ENUM.MALE
        ? "url(#recolor-male)"
        : showPosition && entity.personInfo.sex === SEX_ENUM.FEMALE
          ? "url(#recolor-female)"
          : FILTERS.BLACK_TO_UNKNOWN
      : FILTERS.BLACK_TO_UNKNOWN
    : getStatusLevel(getFullStatus(entity)) === "GOOD"
      ? FILTERS.BLACK_TO_GOOD
      : getStatusLevel(getFullStatus(entity)) === "BAD"
        ? FILTERS.BLACK_TO_BAD
        : getStatusLevel(getFullStatus(entity)) === "WARN"
          ? FILTERS.BLACK_TO_WARN
          : FILTERS.BLACK_TO_UNKNOWN

//#region drag
let d2 = 0;

function dragstarted(event, d) {
  if (!event.active) nexus.simulation.alphaTarget(0.3).restart();
  d.fx = d.x;
  d.fy = d.y;
  d2 = 0;
}

function dragged(event, d) {
  d.fx += event.dx;
  d.fy += event.dy;
  d2 += event.dx * event.dx + event.dy * event.dy;

  // Don't let a drag pull the node's circle within 'nodeEdgeMargin' of the plot area edge
  const inset = findNodeRadius(d.level) + layout.nodeEdgeMargin;
  d.fx = Math.max(inset, Math.min(nexus.width - inset, d.fx));
  d.fy = Math.max(inset, Math.min(nexus.height - inset, d.fy));
}

function dragended(event, d) {
  if (!event.active) {
    nexus.simulation.alphaTarget(simConfig.alphaTarget);
  }
  d.fx = null;
  d.fy = null;

  if (d2 < simConfig.minDrag) { // only a small movement, so allow click
    d3.select(window).on('click.drag', null);
  }
}
//#endregion drag